import { Box, Divider, IconButton, Stack, SxProps, Typography } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useRouter } from '@/hooks';
import EditIcon from '@mui/icons-material/Edit';

import { format } from 'date-fns';
import { useAuth0 } from '@auth0/auth0-react';
import * as poiState from '../../../state';

import { FieldViewer } from '../FieldViewer';
import { useCreatedBy } from '@/hooks';
import { convertTimeStampToDate, formatEnumForDisplay, titleCase } from '@/utils';
import { usePoiStatusLabels } from '@/hooks/usePoiStatusLabels';
import { enumToString } from '@/utils/string';
import { checkIfVoi } from '@/utils/poi';
import { useImageFromLocationId } from '@/components/Analysis/Viewer/hooks/useImageFromLocationId';
import { ImageType, PointOfInterest, UnitsEnum } from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { UserAvatar } from '@/components/shared/UserAvatar';

import {
  BLISTER_TYPE,
  NOT_APPLICABLE_LABEL,
  PAINT_BLOCK_LABEL,
  PIT_TYPE,
  UPDATE_LOCATION_LABEL,
  VIEW_IN_3D_TOOLTIP_LABEL,
} from '@/constants';
import { MediaUploadsAndViewer } from './MediaUploadAndViewer';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { useAnalysisCuboidActions } from '@/components/Analysis/modules/cuboids/hooks/useAnalysisCuboidActions';
import { LinkedPois } from './LinkedPois';
import { useTriggerPoiLocationUpdateEffect } from '../../../effects/useTriggerPoiLocationUpdateEffect';
import { useSavePoiScreenshot } from '../../../createOrEdit/useSavePoiScreenshot';
import { useIsPoiWorkScope } from '@/hooks/useIsPoiWorkScope';
import { localiseMeasurement } from '@/utils/unitSystem';
import { ClickEvent } from '@/types';
import { OverviewAccordion } from '@/components/Analysis/ReviewScreen/ComponentsOverview/OverviewAccordion';
import { ComponentReports } from '@/components/Analysis/ReviewScreen/ComponentReviewPanelReports/ComponentReports';

const boxRootStyle = {
  px: '2.4rem',
  py: '3.2rem',

  '& > * + *': {
    marginTop: 2,
  },
  // enable scrolling
  overflowY: 'auto',
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
};

type FieldsType = {
  label: string;
  value: string;
  icon?: ReactNode;
  onClick?: (event: undefined | ClickEvent) => void;
  valueTooltip?: string;
};

type Props = {
  selectedPoiInfo: PointOfInterest;
  selectedPoiInfoLoading?: boolean;
  sx?: SxProps
};

export const GeneralTab = ({ selectedPoiInfo, sx }: Props) => {
  const { getPoiStatusLabel } = usePoiStatusLabels();
  const setEditPointOfInterest = useSetAtom(poiState.editPointOfInterest);
  const allViewpoints = useAtomValue(state.structureLocations);
  const setCameraTarget = useSetAtom(state.cameraTarget);
  const setSelectedSpherical = useSetAtom(state.selectedSpherical);
  const setSelectedAssemblyId = useSetAtom(state.selectedAssemblyId);
  const setSelectedAssemblyName = useSetAtom(state.selectedAssemblyName);
  const selectedAssemblyId = useAtomValue(state.selectedAssemblyId);
  const getSphericalFromId = useImageFromLocationId(allViewpoints);
  const unitConversionSystem = useAtomValue(state.unitSystem);
  const allVoiTemplates = useAtomValue(state.allVoiTemplates);
  const setDrawerRight = useSetAtom(state.drawerRight);

  const [isUpdating, setIsUpdating] = useState(false);
  const { handleSavePoiScreenshot } = useSavePoiScreenshot({ imageType: ImageType.Other });

  const { startEditingPoiCuboids } = useAnalysisCuboidActions();

  const router = useRouter();
  const { user } = useAuth0();
  const navbarHeight = useNavbarHeight();

  const isAtInsightsPage = useMemo(() => {
    return router.pathname.includes('insights');
  }, [router.pathname]);

  // internal view external: they should be able to see external emails
  const createdByLabel = useCreatedBy({
    viewer: user,
    creator: selectedPoiInfo.createdByUser,
  });

  const poiSpherical = useMemo(() => {
    if (!selectedPoiInfo.location?.id) return;
    const spherical = getSphericalFromId(selectedPoiInfo.location.id);
    if (spherical?.name) return spherical;
  }, [getSphericalFromId, selectedPoiInfo.location]);

  const imageName = useMemo(() => {
    return poiSpherical?.name ?? NOT_APPLICABLE_LABEL;
  }, [poiSpherical]);

  const goToImageView = useCallback(
    (event: ClickEvent) => {
      const { position, name: sphericalName } = poiSpherical ?? {};
      if (!poiSpherical || !sphericalName) return;

      const { centerPoint3d, assembly, id: poiId } = selectedPoiInfo;
      const { x, y, z } = centerPoint3d || {};
      if (x === undefined || y === undefined || z === undefined) return;
      const lookAt = { x, y, z };
      setCameraTarget({ position, lookAt: [x, y, z] });
      setSelectedSpherical({ ...poiSpherical, lookAt });
      setSelectedAssemblyId(assembly.id);
      setSelectedAssemblyName(assembly.tagName);

      let url = `/analysis/${router.query.inspection}/viewer`;

      const isCtrlClick = event.ctrlKey || event.metaKey;
      if (isCtrlClick) {
        const lookAtParameters = `&look.x=${x}&look.y=${y}&look.z=${z}`;
        url += `?assembly=${assembly.tagName}&poi=${poiId}${lookAtParameters}&selectedSpherical=${sphericalName}`;
        window.open(url, '_blank');
      } else {
        if (isAtInsightsPage) router.push(url);
      }
    },
    [
      poiSpherical,
      selectedPoiInfo,
      setCameraTarget,
      setSelectedSpherical,
      setSelectedAssemblyId,
      setSelectedAssemblyName,
      router,
      isAtInsightsPage,
    ]
  );

  const selectedPoiHeight = useMemo(() => {
    const height = selectedPoiInfo.deckRelativeHeight;
    return localiseMeasurement({
      unitSystem: unitConversionSystem,
      sourceUnit: UnitsEnum.Meter,
      value: height,
    });
  }, [selectedPoiInfo.deckRelativeHeight, unitConversionSystem]);

  const selectedPoiViewpointRelativeHeight = useMemo(() => {
    const height = selectedPoiInfo.viewpointRelativeHeight;
    return localiseMeasurement({
      unitSystem: unitConversionSystem,
      sourceUnit: UnitsEnum.Meter,
      value: height,
    });
  }, [selectedPoiInfo.viewpointRelativeHeight, unitConversionSystem]);

  const handleUpdateLocation = useCallback(
    (event: ClickEvent | undefined) => {
      if (isAtInsightsPage) {
        if (!event) return;
        // The user is at insights page, redirect them to viewer
        // From there the `useTriggerPoiLocationUpdateEffect` will be triggered
        // this effect will be listening for the `isUpdatingPoiLocation` query param

        const isCtrlClick = event.ctrlKey || event.metaKey;
        const { centerPoint3d } = selectedPoiInfo;
        const cameraParams = centerPoint3d
          ? `&look.x=${centerPoint3d.x}&look.y=${centerPoint3d.y}&look.z=${
              centerPoint3d.z
            }&position.x=${centerPoint3d.x}&position.y=${centerPoint3d.y + 15}&position.z=${
              centerPoint3d.z + 5
            }&fov=0`
          : '';

        if (isCtrlClick) {
          const url = `/analysis/${router.query.inspection}/viewer?assembly=${selectedPoiInfo.assembly.tagName}&poi=${selectedPoiInfo.id}&isUpdatingPoiLocation=true${cameraParams}`;
          window.open(url, '_blank');
          return;
        } else {
          router.push({
            pathname: `/analysis/${router.query.inspection}/viewer`,
            query: {
              assembly: selectedPoiInfo.assembly.tagName,
              isUpdatingPoiLocation: 'true',
              poi: selectedPoiInfo.id,
              ...(centerPoint3d && {
                'look.x': String(centerPoint3d.x),
                'look.y': String(centerPoint3d.y),
                'look.z': String(centerPoint3d.z),
                'position.x': String(centerPoint3d.x),
                'position.y': String(centerPoint3d.y + 15),
                'position.z': String(centerPoint3d.z + 5),
                fov: '0',
              }),
            },
          });
        }

        return;
      }
      setDrawerRight({
        equipment: false,
        viewpoints: false,
        measuringTool: false,
        findViewpointsTool: false,
      });

      if (checkIfVoi(allVoiTemplates, selectedPoiInfo?.type)) {
        const voiTemplate = allVoiTemplates?.find((item) => item.id === selectedPoiInfo.templateId);
        setEditPointOfInterest((previous) => ({
          ...previous,
          template: voiTemplate,
          pointOfInterest: selectedPoiInfo,
          formState: 'Resizing-VOI',
          state: 'WaitingForPoint',
        }));
      } else {
        setEditPointOfInterest((previous) => ({
          ...previous,
          formState: 'Update-Location',
          state: 'WaitingForPoint',
        }));
      }
    },
    [
      isAtInsightsPage,
      router,
      setDrawerRight,
      setEditPointOfInterest,
      selectedPoiInfo,
      allVoiTemplates,
    ]
  );

  const { checkByType: isPoiTypeOfWorkScope } = useIsPoiWorkScope();

  const isVoi = checkIfVoi(allVoiTemplates, selectedPoiInfo?.type);

  const isPaintBlockVoi = isPoiTypeOfWorkScope(selectedPoiInfo?.type);

  const shouldIncludeCuboids = useMemo(() => {
    return isVoi || isPaintBlockVoi;
  }, [isVoi, isPaintBlockVoi]);

  const createdByAvatar = useMemo(() => {
    return selectedPoiInfo.createdByUser?.roleIds?.some(
      (role) => role?.name === 'api-inspector'
    ) ? (
      <UserAvatar
        name={selectedPoiInfo.createdByUser?.name}
        picture={selectedPoiInfo.createdByUser?.picture}
        sx={{ width: '2.5rem', height: '2.5rem' }}
      />
    ) : (
      <UserAvatar
        picture="/assets/abyss_fabric_mini_logo_white.svg"
        sx={{ width: '2.5rem', height: '2.5rem', background: '#000', padding: '0.25rem' }}
      />
    );
  }, [selectedPoiInfo.createdByUser]);

  const generalFields = useMemo(() => {
    const convertedDueDate = convertTimeStampToDate(selectedPoiInfo.dueDate);
    const formattedDueDate = convertedDueDate
      ? format(convertedDueDate, 'dd/MMM/yyyy')
      : NOT_APPLICABLE_LABEL;

    const convertedCreatedAtData = convertTimeStampToDate(selectedPoiInfo.createdAt);
    const formattedCreatedAt = convertedCreatedAtData
      ? format(convertedCreatedAtData, 'dd/MMM/yyyy')
      : NOT_APPLICABLE_LABEL;

    const {
      poiId,
      name,
      paintRegion,
      parentPaintRegionId,
      assembly,
      status,
      visibility,
      assignee,
      comment,
      accessibility,
      type,
      centerPoint3d,
    } = selectedPoiInfo;

    let poiHasLocation = true;
    const coordinates = centerPoint3d;

    // if no coordinates, POI was uploaded via CSV
    // hence, no location
    if (!coordinates) poiHasLocation = false;
    else if (coordinates?.x === 0 && coordinates?.y === 0 && coordinates?.z === 0)
      poiHasLocation = false;

    const fields: FieldsType[] = [
      {
        label: 'POI ID',
        value: poiId ?? NOT_APPLICABLE_LABEL,
      },
      {
        label: 'Name',
        value: name ?? NOT_APPLICABLE_LABEL,
      },
      {
        label: PAINT_BLOCK_LABEL,
        value: paintRegion?.name ?? NOT_APPLICABLE_LABEL,
      },
      ...(shouldIncludeCuboids
        ? [
            {
              label: 'Parent Paint Block',
              value: parentPaintRegionId?.name ?? NOT_APPLICABLE_LABEL,
            },
          ]
        : []),
      {
        label: 'Component',
        value: assembly?.tagName ?? NOT_APPLICABLE_LABEL,
      },
      {
        label: 'Image Name',
        value: imageName,
        onClick: goToImageView as () => void,
        valueTooltip: imageName === NOT_APPLICABLE_LABEL ? '' : VIEW_IN_3D_TOOLTIP_LABEL,
      },
      {
        label: 'Due Date',
        value: formattedDueDate,
      },
      {
        label: 'Status',
        value: status ? getPoiStatusLabel(status, type) : NOT_APPLICABLE_LABEL,
      },
      {
        label: 'Workpack',
        value: selectedPoiInfo.workpack?.name ?? NOT_APPLICABLE_LABEL,
      },
      {
        label: 'Access Control',
        value: titleCase(visibility) ?? NOT_APPLICABLE_LABEL,
      },
      {
        label: 'Assigned To',
        value: assignee ?? NOT_APPLICABLE_LABEL,
      },
      {
        label: 'Created By',
        value: createdByLabel,
        icon: createdByAvatar,
        valueTooltip: selectedPoiInfo.createdByUser?.email,
      },
      {
        label: 'Comment',
        value: comment ?? NOT_APPLICABLE_LABEL,
      },
      {
        label: 'Accessibility',
        value: formatEnumForDisplay(accessibility) ?? NOT_APPLICABLE_LABEL,
      },
      {
        label: 'Date Created',
        value: formattedCreatedAt,
      },
    ];
    if (!isPaintBlockVoi) {
      fields.push(
        {
          label: 'POI Height from Deck',
          value: selectedPoiHeight ?? NOT_APPLICABLE_LABEL,
        },
        {
          label: 'POI Height from Viewpoint',
          value: selectedPoiViewpointRelativeHeight ?? NOT_APPLICABLE_LABEL,
        }
      );
    }
    const poiTypeString = enumToString(type);
    const disallowedTypes = [BLISTER_TYPE, PIT_TYPE];

    if (!disallowedTypes.includes(poiTypeString)) {
      fields.push({
        label: UPDATE_LOCATION_LABEL,
        value: poiHasLocation ? 'true' : 'false',
        onClick: handleUpdateLocation,
      });
    }

    return fields;
  }, [
    selectedPoiInfo,
    imageName,
    goToImageView,
    createdByLabel,
    isPaintBlockVoi,
    selectedPoiHeight,
    selectedPoiViewpointRelativeHeight,
    handleUpdateLocation,
    createdByAvatar,
    getPoiStatusLabel,
    shouldIncludeCuboids,
  ]);

  const startGeneralFieldsEditing = useCallback(() => {
    const voiTemplate = allVoiTemplates?.find((item) => item.id === selectedPoiInfo.templateId);
    setEditPointOfInterest((previous) => ({
      ...previous,
      pointOfInterest: selectedPoiInfo,
      formState: 'Update-General',
      state: 'WaitingForDetail',
      template: voiTemplate,
    }));
    startEditingPoiCuboids(selectedPoiInfo);
  }, [allVoiTemplates, selectedPoiInfo, setEditPointOfInterest, startEditingPoiCuboids]);

  const isUninitialized = useMemo(() => {
    return selectedPoiInfo.type === BLISTER_TYPE && !selectedPoiInfo.blister;
  }, [selectedPoiInfo.blister, selectedPoiInfo.type]);

  const handleCaptureView = useCallback(async () => {
    await handleSavePoiScreenshot({
      poiId: selectedPoiInfo.id,
      location: 'attachments',
      isUpdating,
      setIsUpdating,
      showSnackbar: true,
    }).then(() => setIsUpdating(false));
  }, [handleSavePoiScreenshot, isUpdating, selectedPoiInfo.id]);

  useTriggerPoiLocationUpdateEffect();

  const enableEdit = useMemo(() => {
    return (selectedAssemblyId && !isUninitialized) || isPaintBlockVoi;
  }, [selectedAssemblyId, isUninitialized, isPaintBlockVoi]);

  if (!selectedPoiInfo) return <p>Unable to locate selected POI</p>;

  return (
    <Box sx={{ ...boxRootStyle, height: `calc(100vh - ${navbarHeight + 180}px)` }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.6rem' }}>
          Information
        </Typography>
        {enableEdit && (
          <IconButton onClick={startGeneralFieldsEditing}>
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      <FieldViewer fields={generalFields} selectedPoiInfo={selectedPoiInfo} />

      <OverviewAccordion
        title="Reports"
        details={
          <ComponentReports
            componentId={selectedPoiInfo.assembly.id}
            pointOfInterestId={selectedPoiInfo.id}
            initialTab="POINT_OF_INTEREST"
            hideTabs
          />
        }
        defaultExpanded={false}
        componentName="review-panel-poi-overview"
      />

      <Divider sx={{ my: 2 }} />

      {shouldIncludeCuboids && (
        <>
          <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.6rem' }}>
            Linked POIs
          </Typography>
          <LinkedPois linkedPois={selectedPoiInfo.linkedPointOfInterestIds} />
        </>
      )}
      <MediaUploadsAndViewer
        sx={sx}
        key={selectedPoiInfo.id}
        selectedPoiInfo={selectedPoiInfo}
        isUpdating={isUpdating}
        setIsUpdating={setIsUpdating}
        handleCaptureView={handleCaptureView}
      />
    </Box>
  );
};
